/**
 * API client — mirrors app/services/apiClient.ts's auth pattern (ADM-02): the bearer
 * token is always a fresh Firebase ID token (FirebaseAuthGuard verifies it directly,
 * there is no separate backend-issued JWT), plus a best-effort App Check header.
 * No new backend contract — same base URL, same endpoints as the mobile app.
 */
import { auth } from '../config/firebase';
import { env } from '../config/env';
import { getAppCheckToken } from './appCheck';
import {
  ensureRestEncryptionReady,
  encryptWithKey,
  decryptWithKey,
  getRestServerKeyVersion,
} from './payloadCrypto';

export const API_BASE_URL = env.apiBaseUrl;

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public message: string,
    public errors?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Backend login-time 2FA gate (FirebaseAuthGuard step 1.5): a user with 2FA enabled whose ID
 * token has no `tfaVerifiedAt` claim gets 403 with this exact message on every route except
 * `POST /auth/2fa/verify`. The portal reacts centrally (see AuthContext) with a challenge.
 */
export const TWO_FACTOR_REQUIRED_MESSAGE = 'TWO_FACTOR_REQUIRED';

export const isTwoFactorRequiredError = (err: unknown): boolean =>
  err instanceof ApiError && err.statusCode === 403 && err.message === TWO_FACTOR_REQUIRED_MESSAGE;

const twoFactorListeners = new Set<() => void>();

/** Subscribe to "an authenticated request was refused for lack of 2FA verification". */
export const onTwoFactorRequired = (listener: () => void): (() => void) => {
  twoFactorListeners.add(listener);
  return () => {
    twoFactorListeners.delete(listener);
  };
};

/**
 * Backend admin 2FA enrollment gate (behind a default-OFF switch): an admin-capable account
 * that has not enabled 2FA gets 403 with this exact message on every route except the 2FA
 * enrollment endpoints, logout and `/auth/sync`. The portal reacts centrally (see AuthContext)
 * with the enrollment screen. Until the backend sends it, nothing here ever fires.
 */
export const TWO_FACTOR_ENROLLMENT_REQUIRED_MESSAGE = 'TWO_FACTOR_ENROLLMENT_REQUIRED';

export const isTwoFactorEnrollmentRequiredError = (err: unknown): boolean =>
  err instanceof ApiError &&
  err.statusCode === 403 &&
  err.message === TWO_FACTOR_ENROLLMENT_REQUIRED_MESSAGE;

const twoFactorEnrollmentListeners = new Set<() => void>();

/** Subscribe to "an authenticated request was refused because 2FA enrollment is required". */
export const onTwoFactorEnrollmentRequired = (listener: () => void): (() => void) => {
  twoFactorEnrollmentListeners.add(listener);
  return () => {
    twoFactorEnrollmentListeners.delete(listener);
  };
};

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean;
  timeout?: number;
}

// SEC-17 — bootstrap calls that can never be encrypted themselves: /auth/sync
// is what registers the device's public key in the first place, and
// /crypto/server-key is the public, unauthenticated fetch that discovers the
// backend's key. Every other authenticated request/response goes through
// encryptWithKey/decryptWithKey unconditionally once the capability is ready
// — no per-call opt-in flag, unlike the mobile app's narrower PHI-only scope
// (docs/security.md §18.0.1, extended per SEC-16's blanket-coverage decision).
const ENCRYPTION_BOOTSTRAP_ENDPOINTS = ['/auth/sync', '/crypto/server-key'];

const getApiErrorMessage = (data: unknown): string => {
  if (typeof data === 'string') return data;
  const record = data as { message?: unknown; error?: unknown } | undefined;
  const message = record?.message ?? record?.error;
  if (Array.isArray(message)) return message.join(' ');
  return typeof message === 'string' ? message : 'Request failed';
};

export const apiRequest = async <T = unknown>(
  endpoint: string,
  config: RequestConfig = {},
): Promise<T> => {
  const { requiresAuth = true, timeout = 30000, headers = {}, ...rest } = config;

  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Multipart: the browser must set Content-Type itself (it carries the boundary).
  if (typeof FormData !== 'undefined' && rest.body instanceof FormData) {
    delete (requestHeaders as Record<string, string>)['Content-Type'];
  }

  if (requiresAuth) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new ApiError(401, 'Not signed in');
    }
    // Firebase refreshes automatically when the cached ID token is expired.
    const token = await currentUser.getIdToken(false);
    (requestHeaders as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) {
    (requestHeaders as Record<string, string>)['X-Firebase-AppCheck'] = appCheckToken;
  }

  let payloadSharedKey: Uint8Array | null = null;
  if (requiresAuth && !ENCRYPTION_BOOTSTRAP_ENDPOINTS.includes(endpoint)) {
    payloadSharedKey = await ensureRestEncryptionReady();
    if (payloadSharedKey) {
      (requestHeaders as Record<string, string>)['X-Payload-Encryption'] = '1';
      if (typeof rest.body === 'string') {
        const encryptedPayload = await encryptWithKey(rest.body, payloadSharedKey);
        if (encryptedPayload) {
          rest.body = JSON.stringify({ encryptedPayload, keyVersion: getRestServerKeyVersion() });
        }
      }
    }
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { ...rest, headers: requestHeaders, signal: controller.signal });
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    let data = isJson ? await response.json() : await response.text();

    if (
      payloadSharedKey &&
      data &&
      typeof data === 'object' &&
      'encryptedPayload' in (data as Record<string, unknown>)
    ) {
      try {
        const decrypted = await decryptWithKey(
          (data as { encryptedPayload: string }).encryptedPayload,
          payloadSharedKey,
        );
        data = JSON.parse(decrypted);
      } catch (decryptError) {
        console.error('[apiClient] Failed to decrypt response payload:', decryptError);
        throw new ApiError(500, 'Failed to decrypt response');
      }
    }

    if (!response.ok) {
      const apiError = new ApiError(response.status, getApiErrorMessage(data), data);
      if (requiresAuth && isTwoFactorRequiredError(apiError)) {
        twoFactorListeners.forEach((listener) => listener());
      }
      if (requiresAuth && isTwoFactorEnrollmentRequiredError(apiError)) {
        twoFactorEnrollmentListeners.forEach((listener) => listener());
      }
      throw apiError;
    }

    // Backend wraps all responses (except @SkipTransform() routes) with
    // { success, data, timestamp, path } — mirrors app/services/apiClient.ts's unwrap.
    if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
      return (data as { data: T }).data;
    }
    return data as T;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const apiGet = <T = unknown>(endpoint: string, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'GET' });

export const apiPost = <T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'POST', body: body ? JSON.stringify(body) : undefined });

/** Multipart POST. Unlike apiPost the body is sent as-is (never JSON-stringified or payload-encrypted). */
export const apiPostForm = <T = unknown>(endpoint: string, form: FormData, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'POST', body: form });

export const apiPatch = <T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'PATCH', body: body ? JSON.stringify(body) : undefined });

export const apiPut = <T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'PUT', body: body ? JSON.stringify(body) : undefined });

export const apiDelete = <T = unknown>(endpoint: string, config?: RequestConfig) =>
  apiRequest<T>(endpoint, { ...config, method: 'DELETE' });
